import { useCallback, useMemo } from "react";
import type {
    ClientsCloseOverviewDetailsData,
    ClientsCloseOverviewSlimQuery,
    ClientsCloseOverviewSlimData,
} from "@common/schemas/clientsCloseOverview.schema";
import { useKeptRows } from "@hooks/useKeptRows";
import { parseCloseId } from "@hooks/useOpenWorkRow";
import {
    useClientsCloseOverviewDetailsQuery,
    useClientsCloseOverviewSlimQuery,
    useCloseUnreadCountsQuery,
} from "@api/workspace.api";
import {
    type ClientsWorkOverviewWorkspaceRow,
    mapWorkspaceToWorkRow,
} from "./clientsWorkOverviewViewConfig";

const NO_DETAILS: ClientsCloseOverviewDetailsData = { workspaces: [] };

interface UnreadCount {
    closeId: string;
    unreadTaskCount: number;
}

/** One loaded page: its clients with their work, and the counts the footer and pager show. */
interface WorkOverviewPage {
    workspaceRows: ClientsWorkOverviewWorkspaceRow[];
    pageCount: number;
    clientCount: number;
}

const getCloseIds = (details: ClientsCloseOverviewDetailsData) =>
    details.workspaces.flatMap((workspace) =>
        workspace.children.flatMap((entity) =>
            entity.children.map((close) => parseCloseId(close.id) ?? close.id),
        ),
    );

/** The page's clients in the server's order, each with its work and unread counts. */
const buildWorkspaceRows = ({
    slim,
    details,
    unreadCounts,
}: {
    slim: ClientsCloseOverviewSlimData;
    details: ClientsCloseOverviewDetailsData;
    unreadCounts: readonly UnreadCount[];
}): ClientsWorkOverviewWorkspaceRow[] => {
    const detailsMap = new Map(details.workspaces.map((ws) => [ws.id, ws]));
    const unreadMap = new Map(
        unreadCounts.map((count) => [count.closeId, count.unreadTaskCount]),
    );
    return slim.workspaces.map((ws) => {
        const rawId = ws.id;
        const workspaceDetails = detailsMap.get(`workspace:${rawId}`);
        if (workspaceDetails) {
            const row = mapWorkspaceToWorkRow(workspaceDetails);
            row.children = row.children.map((child) => {
                const rawCloseId = parseCloseId(child.closeId) ?? child.closeId;
                return {
                    ...child,
                    unreadTaskCount: unreadMap.get(rawCloseId) ?? 0,
                };
            });
            return row;
        }
        return {
            id: `workspace:${rawId}`,
            rowType: "workspace" as const,
            name: ws.name,
            workspaceId: rawId,
            children: [],
        };
    });
};

/**
 * The Work page's clients with their work, loaded in two steps: the page of
 * clients (slim), then their work items (details) and unread counts. Rows
 * show only once both steps answered the current request. Until then, a
 * filter, search, page or sort change keeps the last rows, marked
 * refetching; the first load shows the loading state.
 */
export const useWorkOverviewRows = (
    listQuery: ClientsCloseOverviewSlimQuery,
) => {
    const slimQuery = useClientsCloseOverviewSlimQuery(listQuery);
    const slim = slimQuery.isPlaceholderData ? undefined : slimQuery.data;
    const workspaceIds = useMemo(
        () => slim?.workspaces.map((w) => w.id) ?? [],
        [slim],
    );
    const hasWorkspaces = workspaceIds.length > 0;

    const detailsQuery = useClientsCloseOverviewDetailsQuery(
        {
            organizationId: listQuery.organizationId,
            workspaceIds,
            filters: listQuery.filters,
        },
        { enabled: hasWorkspaces },
    );
    const details = hasWorkspaces ? detailsQuery.data : NO_DETAILS;
    const closeIds = useMemo(
        () => (details ? getCloseIds(details) : []),
        [details],
    );
    const { data: unreadCountsData } = useCloseUnreadCountsQuery({
        organizationId: listQuery.organizationId,
        closeIds,
    });

    const currentPage = useMemo((): WorkOverviewPage | null => {
        if (!slim || !details) {
            return null;
        }
        return {
            workspaceRows: buildWorkspaceRows({
                slim,
                details,
                unreadCounts: unreadCountsData?.counts ?? [],
            }),
            pageCount: slim.pageCount,
            clientCount: slim.total,
        };
    }, [slim, details, unreadCountsData]);
    const { rows: shownPage, isRefetching } = useKeptRows({
        rows: currentPage,
        datasetKey: listQuery.organizationId,
    });

    const hasSlimFailed = slimQuery.isError && slimQuery.data === undefined;
    const haveDetailsFailed =
        hasWorkspaces &&
        detailsQuery.isError &&
        detailsQuery.data === undefined;
    const { refetch: refetchSlim } = slimQuery;
    const { refetch: refetchDetails } = detailsQuery;
    const retry = useCallback(() => {
        void (hasSlimFailed ? refetchSlim() : refetchDetails());
    }, [hasSlimFailed, refetchSlim, refetchDetails]);

    return {
        /** `null` while there's nothing to show yet. */
        workspaceRows: shownPage?.workspaceRows ?? null,
        /** The rows are the last request's while this one loads. */
        isRefetching,
        /** The rows answer the current request. */
        areRowsCurrent: currentPage !== null,
        isError: hasSlimFailed || haveDetailsFailed,
        retry,
        pageCount: shownPage?.pageCount ?? 0,
        clientCount: shownPage?.clientCount ?? 0,
    };
};
